import { getKidSub } from '@/auth/authStore';
import { api } from '@/lib/api';
import type {
  StudyPracticeKind,
  StudyPracticeSet,
  StudyPracticeQuestion,
  StudyPracticeOption,
} from './studyAnalysisFollowUpApi';

export interface SelfAssessmentConfig {
  title: string;
  question_count: number;
  question_kinds: StudyPracticeKind[];
  difficulty: 'easy' | 'standard' | 'challenge';
  /** Empty selects the whole analysed material. Indices refer to its displayed key points. */
  key_point_indices: number[];
}
export interface AssessmentDraftQuestion {
  id?: string;
  kind: StudyPracticeKind;
  stem: string;
  options: string[] | null;
  correct_option: StudyPracticeOption | null;
  answer: string;
  worked_solution: string;
}
export interface SelfAssessmentPaper extends StudyPracticeSet {
  assessment: SelfAssessmentConfig;
  phase: 'draft' | 'published';
  revision: number;
  check_available?: boolean;
  draft_questions?: AssessmentDraftQuestion[];
}
export interface SelfAssessmentQuote {
  assessment: SelfAssessmentConfig;
  stars: number;
  question_count: number;
  check_stars_per_attempt: number;
}
export const selfAssessmentsKey = (subject: string) =>
  ['study-self-assessments', getKidSub(), subject] as const;
export const selfAssessmentKey = (analysis: string, set: string) =>
  ['study-self-assessment', getKidSub(), analysis, set] as const;
export const selfAssessmentPath = (analysis: string, set: string, subject = 'mathematics') =>
  `/learn/study/self-assessments/${encodeURIComponent(analysis)}/${encodeURIComponent(set)}?subject=${encodeURIComponent(subject)}`;
const endpoint = (analysis: string, set: string) =>
  `/study/analyses/${encodeURIComponent(analysis)}/self-assessments/${encodeURIComponent(set)}`;
export const listSelfAssessments = (subject: string) =>
  api<{ items: SelfAssessmentPaper[]; limit: number }>(
    `/study/self-assessments?subject_key=${encodeURIComponent(subject)}`,
  );
export const readSelfAssessment = (analysis: string, set: string) =>
  api<SelfAssessmentPaper>(endpoint(analysis, set));
export const quoteSelfAssessment = (analysis: string, assessment: SelfAssessmentConfig) =>
  api<SelfAssessmentQuote>(`/study/analyses/${encodeURIComponent(analysis)}/practice-sets/quote`, {
    method: 'POST',
    body: { assessment },
  });
export const generateSelfAssessment = (
  analysis: string,
  assessment: SelfAssessmentConfig,
  stars: number,
  key: string,
) =>
  api<SelfAssessmentPaper>(`/study/analyses/${encodeURIComponent(analysis)}/practice-sets`, {
    method: 'POST',
    body: { assessment, expected_stars: stars, idempotency_key: key },
  });
export const saveSelfAssessment = (
  paper: SelfAssessmentPaper,
  title: string,
  questions: AssessmentDraftQuestion[],
) =>
  api<SelfAssessmentPaper>(endpoint(paper.analysis_id, paper.id), {
    method: 'PUT',
    body: {
      expected_revision: paper.revision,
      title,
      questions: questions.map(({ id: _id, ...question }) => question),
    },
  });
export const startSelfAssessment = (paper: SelfAssessmentPaper) =>
  api<SelfAssessmentPaper>(`${endpoint(paper.analysis_id, paper.id)}/start`, {
    method: 'POST',
    body: { expected_revision: paper.revision },
  });
export function assessmentScore(questions: StudyPracticeQuestion[]) {
  return {
    correct: questions.filter(
      (q) => q.latest_attempt?.status === 'checked' && q.latest_attempt.verdict === 'correct',
    ).length,
    uncertain: questions.filter(
      (q) =>
        q.latest_attempt?.status === 'checked' &&
        ['cant_tell', 'incomplete'].includes(q.latest_attempt.verdict ?? ''),
    ).length,
    checked: questions.filter((q) => q.latest_attempt?.status === 'checked').length,
    total: questions.length,
  };
}
